import React, { useCallback, useLayoutEffect, useRef } from "react";
import { aiButtonWaits, aiLabelOf, startAiJob, useAiJobs } from "../hooks/useAiWork";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "danger-solid";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /**
     * How much the action matters on the screen — not a colour choice.
     * - primary: the one main action of an area (Save, Create mission)
     * - secondary (default): every other action
     * - ghost: navigation / toolbar actions that shouldn't add weight (Back, Columns)
     * - danger: a destructive action on a page (outlined)
     * - danger-solid: the confirm button of a destructive dialog
     */
    variant?: ButtonVariant;
    /**
     * xs 26px (table rows) · sm 30px (matches inputs) · md 36px (action bars,
     * dialogs) · lg 44px. Omit to inherit the container's size: the record
     * action bar and modal footers render md, everything else sm.
     */
    size?: ButtonSize;
    /** Bootstrap Icons name without the `bi-` prefix, e.g. "plus-lg". */
    icon?: string;
    /**
     * Shows a spinner in place of the icon and blocks clicks, keeping the variant's look.
     * On an AI button (one carrying the "AI · N left" mark) it also locks the panel,
     * drawer or dialog it sits in and makes other AI buttons and main buttons wait.
     */
    loading?: boolean;
    /** Label while `loading` (e.g. "Saving…"); defaults to the normal label. */
    loadingText?: React.ReactNode;
}

/**
 * The shared CRX button: a native <button> styled by the button system in
 * theme.scss. Like a native button it has no default `type`, so pass
 * type="button" for buttons inside a form that must not submit it.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ variant = "secondary", size, icon, loading = false, loadingText, disabled, className, title, children, ...rest }, ref) => {
        const innerRef = useRef<HTMLButtonElement | null>(null);
        const isAiRef = useRef(false);
        const aiLabelRef = useRef("");
        const aiJobs = useAiJobs();

        const setRefs = useCallback((element: HTMLButtonElement | null) => {
            innerRef.current = element;
            if (typeof ref === "function") ref(element);
            else if (ref) ref.current = element;
        }, [ref]);

        // The AI mark inside makes this an AI button; read it while idle, before a loadingText replaces it.
        useLayoutEffect(() => {
            if (loading || !innerRef.current) return;
            isAiRef.current = !!innerRef.current.querySelector("[data-ai-mark]");
            if (isAiRef.current) aiLabelRef.current = aiLabelOf(innerRef.current);
        });

        // before paint, so nothing is clickable a frame early or late
        useLayoutEffect(() => {
            if (loading && isAiRef.current && innerRef.current) return startAiJob(innerRef.current, aiLabelRef.current);
        }, [loading]);

        // reads the last committed DOM: every button re-renders when an AI job starts or ends
        const isWaiting = !loading && aiButtonWaits(innerRef.current, aiJobs, isAiRef.current, variant === "primary");

        const classes = [
            "crx-btn",
            `crx-btn--${variant}`,
            size ? `crx-btn--${size}` : "",
            loading ? "is-loading" : "",
            className ?? "",
        ].filter(Boolean).join(" ");

        return (
            <button ref={setRefs} className={classes} disabled={disabled || loading || isWaiting} aria-busy={loading || undefined}
                title={isWaiting ? "Wait for the AI to finish. One AI job at a time." : title} {...rest}>
                {loading ? (
                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                ) : (
                    icon && <i className={`bi bi-${icon}`} aria-hidden="true" />
                )}
                {loading && loadingText !== undefined ? loadingText : children}
            </button>
        );
    }
);

Button.displayName = "Button";

export default Button;
